import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
} from '@mui/material';
import { CollectionGroupVO } from '../../api/collectionApi';
import { useTranslation } from 'react-i18next';

interface GroupEditDialogProps {
    open: boolean;
    group: CollectionGroupVO | null; // null for creating new
    error?: string | null; // Error message to display
    onClose: () => void;
    onSave: (name: string) => void;
}

const GroupEditDialog: React.FC<GroupEditDialogProps> = ({
    open,
    group,
    error,
    onClose,
    onSave,
}) => {
    const { t } = useTranslation(['navigation', 'common']);
    const [name, setName] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setName(group?.name || '');
            setLocalError(null); // Reset error when dialog opens
        }
    }, [group, open]);

    // Sync external error to local state
    useEffect(() => {
        setLocalError(error || null);
    }, [error]);

    const handleSave = () => {
        if (name.trim()) {
            onSave(name.trim());
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && name.trim()) {
            e.preventDefault();
            handleSave();
        }
        if (e.key === 'Escape') {
            onClose();
        }
    };

    const isEditing = group !== null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1.5, pt: 2.5 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
                    {isEditing ? t('navigation:editGroup') : t('navigation:addGroup')}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 1.5, pb: 2 }}>
                <TextField
                    autoFocus
                    placeholder={t('navigation:groupName')}
                    fullWidth
                    variant="outlined"
                    value={name}
                    error={!!localError}
                    helperText={localError}
                    onChange={(e) => {
                        setName(e.target.value);
                        setLocalError(null); // Clear error when user types
                    }}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        sx: {
                            borderRadius: '8px',
                            fontSize: '14px',
                            '& fieldset': {
                                borderColor: 'rgba(0,0,0,0.12)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(59, 130, 246, 0.4) !important',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#3b82f6 !important',
                                borderWidth: '1px !important',
                            },
                            '&.Mui-error fieldset': {
                                borderColor: '#ef4444 !important',
                            },
                        },
                    }}
                    FormHelperTextProps={{
                        sx: {
                            fontSize: '12px',
                            mt: 0.5,
                            mx: 0,
                        },
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2, gap: 0.75 }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: '#64748b',
                        fontWeight: 500,
                        px: 2,
                        py: 0.625,
                        borderRadius: '6px',
                        fontSize: '13px',
                        textTransform: 'none',
                        '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.04)',
                        },
                    }}
                >
                    {t('common:cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!name.trim()}
                    sx={{
                        bgcolor: '#3b82f6',
                        fontWeight: 600,
                        px: 2.5,
                        py: 0.625,
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontSize: '13px',
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#2563eb',
                            boxShadow: 'none',
                        },
                        '&:disabled': {
                            bgcolor: '#e5e7eb',
                            color: '#9ca3af',
                        },
                    }}
                >
                    {isEditing ? t('common:save') : t('common:add')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GroupEditDialog;
